import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { AppNav } from '@/components/AppNav';
import { fetchFeed, fetchTimelapse, requestTimelapse, type Photo } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useT } from '@/i18n/context';
import { translateError, withDetail } from '@/i18n/errors';
import type { MessageKey } from '@/i18n/types';
import {
  canRenderLocally,
  renderTimelapse,
  selectRange,
  type TimelapseRange,
} from '@/lib/timelapse';

const RANGES: { value: TimelapseRange; label: MessageKey }[] = [
  { value: 'week', label: 'timelapse.range_week' },
  { value: 'month', label: 'timelapse.range_month' },
  { value: 'year', label: 'timelapse.range_year' },
  { value: 'all', label: 'timelapse.range_all' },
];

type Stage =
  | { kind: 'idle' }
  | { kind: 'rendering'; done: number; total: number }
  | { kind: 'waiting' }
  | { kind: 'done'; url: string; local: boolean }
  | { kind: 'failed'; message: string };

export function TimelapsePage() {
  const { data: user } = useSession();
  const t = useT();
  const [range, setRange] = useState<TimelapseRange>('month');
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [serverJobId, setServerJobId] = useState<number | null>(null);

  // Revoked on replacement and unmount: each render is a whole MP4 held in
  // memory, and a few abandoned ones add up to hundreds of megabytes.
  const objectUrl = useRef<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const { data: feed } = useQuery({
    queryKey: ['photos', 'feed', 'all'],
    // The renderer needs full-size URLs, which the feed already returns.
    queryFn: () => fetchFeed(null),
  });

  useEffect(
    () => () => {
      abort.current?.abort();

      if (objectUrl.current) {
        URL.revokeObjectURL(objectUrl.current);
      }
    },
    [],
  );

  const publish = (blob: Blob) => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
    }

    objectUrl.current = URL.createObjectURL(blob);
    setStage({ kind: 'done', url: objectUrl.current, local: true });
  };

  const start = async () => {
    const photos: Photo[] = feed?.photos ?? [];
    const today = user?.today ?? new Date().toISOString().slice(0, 10);
    const frames = selectRange(photos, range, today);

    if (frames.length === 0) {
      setStage({ kind: 'failed', message: t('errors.range_empty') });

      return;
    }

    if (!(await canRenderLocally())) {
      // No WebCodecs: hand it to the worker instead of refusing outright.
      await startOnServer();

      return;
    }

    const controller = new AbortController();
    abort.current = controller;
    setStage({ kind: 'rendering', done: 0, total: frames.length });

    try {
      const blob = await renderTimelapse(frames, {
        signal: controller.signal,
        onProgress: (done, total) => setStage({ kind: 'rendering', done, total }),
      });

      publish(blob);
    } catch (cause) {
      if (controller.signal.aborted) {
        setStage({ kind: 'idle' });

        return;
      }

      setStage({
        kind: 'failed',
        message: translateError(t, cause, 'timelapse.render_failed'),
      });
    }
  };

  const startOnServer = async () => {
    setStage({ kind: 'waiting' });

    try {
      const { timelapse } = await requestTimelapse(range);
      setServerJobId(timelapse.id);
    } catch (cause) {
      setStage({
        kind: 'failed',
        message: translateError(t, cause, 'timelapse.start_failed'),
      });
    }
  };

  // Polled rather than pushed: a render takes seconds, and a websocket for one
  // occasional status change would be a lot of moving parts for the same answer.
  useQuery({
    queryKey: ['timelapse', serverJobId],
    queryFn: async () => {
      const { timelapse } = await fetchTimelapse(serverJobId ?? 0);

      if (timelapse.status === 'ready' && timelapse.url) {
        setStage({ kind: 'done', url: timelapse.url, local: false });
        setServerJobId(null);
      }

      if (timelapse.status === 'failed') {
        setStage({
          kind: 'failed',
          // The job's own `error` is whatever the exception said on the queue
          // worker — FFmpeg's stderr, a storage failure — so it is a detail
          // behind a translated sentence, not the sentence itself.
          message: withDetail(t('timelapse.render_failed'), timelapse.error),
        });
        setServerJobId(null);
      }

      return timelapse;
    },
    enabled: serverJobId !== null,
    refetchInterval: 2000,
  });

  const busy = stage.kind === 'rendering' || stage.kind === 'waiting';

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-6 pb-24">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">{t('timelapse.title')}</h1>
        <p className="text-sm opacity-60">{t('timelapse.subtitle')}</p>
      </header>

      <fieldset className="flex flex-col gap-2" disabled={busy}>
        <legend className="pb-2 text-sm font-medium">{t('timelapse.range')}</legend>

        <div className="grid grid-cols-4 gap-1.5">
          {RANGES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={range === option.value}
              onClick={() => setRange(option.value)}
              className={`rounded-lg px-2 py-2.5 text-sm ${
                range === option.value
                  ? 'bg-current/15 font-medium'
                  : 'bg-current/5 opacity-70'
              }`}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      </fieldset>

      <button
        type="button"
        disabled={busy}
        onClick={() => void start()}
        className="rounded-full bg-current/10 px-6 py-3.5 font-medium disabled:opacity-40"
      >
        {busy ? t('timelapse.rendering') : t('timelapse.render')}
      </button>

      {stage.kind === 'rendering' && (
        <div className="flex flex-col gap-2">
          <progress
            value={stage.done}
            max={stage.total}
            className="w-full"
            aria-label={t('timelapse.progress_label')}
          />
          <p className="text-center text-sm opacity-60">
            {t('timelapse.frames', { done: stage.done, n: stage.total })}
          </p>
          <button
            type="button"
            onClick={() => abort.current?.abort()}
            className="text-sm underline opacity-60"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      {stage.kind === 'waiting' && (
        <p className="text-center text-sm opacity-60">{t('timelapse.on_server')}</p>
      )}

      {stage.kind === 'failed' && (
        <p role="alert" className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {stage.message}
        </p>
      )}

      {stage.kind === 'done' && (
        <section className="flex flex-col gap-3">
          <video
            src={stage.url}
            controls
            playsInline
            loop
            className="w-full rounded-2xl bg-current/5"
          />

          <a
            href={stage.url}
            download={`selflapse-${range}.mp4`}
            className="rounded-full bg-current/10 px-6 py-3.5 text-center font-medium"
          >
            {t('timelapse.download')}
          </a>

          {stage.local && (
            <p className="text-center text-xs opacity-50">{t('timelapse.local')}</p>
          )}
        </section>
      )}

      <AppNav />
    </main>
  );
}
